import type { FC } from 'react';
import type { IEvaultApi } from './use-evault-api';
import type { IVault, IVaultDetail, IVaultStatus } from './types';

import { useCallback, useEffect, useState } from 'react';

import { Modal } from 'views/design-system';

import { EVAULT_API } from './use-evault-api';

/**
 * Archive a vault, and put it back (§8c, R8).
 *
 * Both are `PATCH /vaults/:id {status}` and both are reversible, which is what
 * the copy has to say: archiving is not a delete, and the vault's documents stay
 * stored and reachable. The one consequence worth stating out loud is the name —
 * the unique index is partial on `status: 'active'`, so archiving genuinely
 * frees the name for reuse and restoring genuinely reserves it again. That is
 * also why restore can fail with a 409: someone may have taken the name in
 * between, and the server's own sentence says so.
 */
export interface IArchiveModalProps {
	api: IEvaultApi;
	open: boolean;
	vault: IVault | IVaultDetail | null;
	mode: 'archive' | 'restore';
	onClose: () => void;
	onDone: (vault: IVaultDetail, next: IVaultStatus) => void;
}

export const ArchiveModal: FC<IArchiveModalProps> = ({
	api,
	open,
	vault,
	mode,
	onClose,
	onDone,
}) => {
	const [error, setError] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);

	const isArchive = mode === 'archive';
	const next: IVaultStatus = isArchive ? 'archived' : 'active';

	useEffect(() => {
		if (!open) return;
		setError('');
		setIsSubmitting(false);
	}, [open]);

	const close = useCallback(() => {
		if (isSubmitting) return;
		onClose();
	}, [isSubmitting, onClose]);

	const submit = useCallback(async () => {
		if (!vault?._id) return;
		setIsSubmitting(true);
		const result = await api.patch<IVaultDetail>(
			`${EVAULT_API.VAULTS}/${vault._id}`,
			{ status: next }
		);
		setIsSubmitting(false);

		if (result.kind === 'ok') {
			onDone(result.data, next);
			return;
		}
		/* Inline, not a toast: a 409 here names the vault that took the name, and
		   the reader is standing in the dialog that caused it. */
		setError(result.message);
	}, [api, next, onDone, vault]);

	return (
		<Modal
			open={open}
			title={`${isArchive ? 'Archive' : 'Restore'} ${vault?.name ?? ''}?`}
			size="narrow"
			className="evault-modal"
			onClose={close}
			footer={
				<>
					<button
						className="dbtn"
						type="button"
						disabled={isSubmitting}
						onClick={close}
					>
						Cancel
					</button>
					<button
						className="dbtn pri"
						type="button"
						disabled={isSubmitting}
						onClick={() => void submit()}
					>
						{isArchive ? 'Archive' : 'Restore'}
					</button>
				</>
			}
		>
			<div className="mbody">
				{isArchive
					? 'The vault and its documents stay stored and reachable from the Archived tab. Archiving frees the name for reuse.'
					: 'It returns to the Active tab and its name is reserved again.'}
			</div>
			{!!error && <div className="merr">{error}</div>}
		</Modal>
	);
};

export default ArchiveModal;
